import React from 'react';

/** `m:ss` — recordings are capped under a minute, so no hour case exists. */
const formatElapsed = (ms: number): string => {
  const totalSec = Math.max(0, Math.round(ms / 1000));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

/** Elapsed time turns red only once the cap is close — the limit surfaces when it matters, not before. */
const CAP_WARNING_MS = 10000;
const BAR_MIN_PX = 2;
const BAR_MAX_PX = 16;

export interface RobbieRecordingIndicatorProps {
  /** Real mic input levels (0..1), oldest first — a still row under reduced motion. */
  levels: number[];
  elapsedMs: number;
  /** The recorder's hard cap; the elapsed time warns as it approaches. */
  maxDurationMs?: number;
  className?: string;
}

/**
 * Replaces the "Ask Robbie…" placeholder while the mic is live: a live dot, a
 * level meter driven by the actual input (not a fake loop), and elapsed time.
 * The meter's lines are centred on the row's midline rather than stood on a
 * baseline — it reads as sound passing through, and it stays symmetrical and
 * calm when the room is quiet. The oldest samples fade out so the newest edge
 * is where the eye lands.
 *
 * Deliberately `danger` ink, not `signal` azure, even though this isn't an
 * error: azure at this weight read as faint/disabled rather than live, and
 * "recording is happening" needs the near-universal red convention so a
 * glance at the row is unambiguous. This is the one place `danger` is bent
 * away from meaning "something went wrong" — don't reuse this reasoning
 * elsewhere; every other `danger` usage should stay an actual error. The
 * RobbieMark's LISTENING ring stays azure — Robbie listening is a genuine
 * live signal, not this bent case.
 */
export const RobbieRecordingIndicator: React.FC<RobbieRecordingIndicatorProps> = ({
  levels,
  elapsedMs,
  maxDurationMs = 60000,
  className = '',
}) => {
  const nearCap = maxDurationMs - elapsedMs <= CAP_WARNING_MS;

  return (
    <div role="status" className={`flex min-w-0 flex-1 items-center gap-s3 ${className}`}>
      <span className="sr-only">Recording</span>
      <span
        aria-hidden="true"
        className="h-[6px] w-[6px] shrink-0 rounded-round bg-danger-ink motion-safe:animate-pulse"
      />
      <div className="flex h-[18px] min-w-0 flex-1 items-center gap-[2px]" aria-hidden="true">
        {levels.map((level, i) => (
          <span
            key={i}
            className="min-w-[1px] flex-1 rounded-round bg-danger-ink"
            style={{
              height: `${Math.round(BAR_MIN_PX + Math.min(1, Math.max(0, level)) * (BAR_MAX_PX - BAR_MIN_PX))}px`,
              // Oldest samples trail off, so the live edge is unmistakable.
              opacity: levels.length > 1 ? 0.28 + (i / (levels.length - 1)) * 0.72 : 1,
            }}
          />
        ))}
      </div>
      <span
        aria-hidden="true"
        className={`shrink-0 font-mono text-[11px] font-medium tabular-nums ${nearCap ? 'text-danger-ink' : 'text-ink-secondary'}`}
      >
        {formatElapsed(elapsedMs)}
      </span>
    </div>
  );
};

export default RobbieRecordingIndicator;
